import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ErrorMessage } from '../components/ErrorMessage'
import { ApiError, apiErrorMessage } from '../api/client'
import { authApi } from '../api/endpoints'
import { AuthLayout } from '../components/AuthLayout'
import { JoinCodeStep } from '../components/auth/JoinCodeStep'
import { StoreBadges } from '../components/StoreBadges'
import { ROLE_OPTIONS } from '../auth/roleOptions'
import { usePageMeta } from '../hooks/usePageMeta'
import type { EntityKind } from '../types/api'

export function LoginPage() {
  usePageMeta(
    'Sign In | YellowDots',
    'Sign in to your YellowDots account to connect with tutors, coaches, trainers and learners near you.',
  )
  const { login, token, loading } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  // Empty means "whichever I was last on", which is what someone holding one role always wants and
  // what everybody got before roles were selectable. Sent as undefined rather than '' - the field
  // is optional on the server and an empty string is not a role.
  const [role, setRole] = useState<EntityKind | ''>('')
  const [error, setError] = useState<string | null>(null)
  // The email has no account: say so, and point at sign-up rather than waiting for a code.
  const [notRegistered, setNotRegistered] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Set once a join code has been requested: the form gives way to the code box.
  const [codeSent, setCodeSent] = useState(false)

  // A token already in localStorage is checked against the server by AuthContext's own startup
  // refreshProfile(); while that's in flight, hold off rendering the form so a person who is
  // already signed in doesn't see it flash before being sent on. If it's still here once that
  // settles, the session is live, so there is nothing left for this page to verify - skip
  // straight past the email+code round trip rather than making them sit through it again.
  if (loading) return null
  if (token) {
    return <Navigate to="/dashboard" replace />
  }

  /** Asks for a join code - sent only when the email has an account; otherwise, sign up first. */
  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setNotRegistered(false)
    setSubmitting(true)
    try {
      await authApi.requestLoginCode(email.trim(), role || undefined)
      setCodeSent(true)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotRegistered(true)
      else setError(apiErrorMessage(err, 'Could not send a code. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function verify(code: string) {
    await login({ email: email.trim(), code, role: role || undefined })
    navigate('/dashboard')
  }

  async function resend() {
    await authApi.requestLoginCode(email.trim(), role || undefined)
  }

  return (
    <AuthLayout>
      <h1 className="text-3xl font-extrabold tracking-tight text-ink-900">
        {codeSent ? 'Email Verification' : 'Sign in'}
      </h1>
      <p className="mt-1 text-ink-500">
        {codeSent
          ? 'Continue with your email to receive a verification code'
          : 'Welcome back. We will email you a join code to log in to your YellowDots account.'}
      </p>

      <div className="mt-6">
        {codeSent ? (
          <JoinCodeStep
            sentTo={email.trim()}
            onVerify={verify}
            onResend={resend}
            onBack={() => setCodeSent(false)}
            submitLabel="Sign In"
            codeLabel="Enter the code sent to your email"
            buttonClassName="btn-sun w-full"
          />
        ) : (
          <form onSubmit={onSubmit} className="space-y-3.5">
            <label className="block">
              <span className="label label-required">Email</span>
              <input
                required
                type="email"
                id="email"
                name="username"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setNotRegistered(false)
                }}
                className="input"
                placeholder="you@example.com"
              />
            </label>

            {/* Optional, and last, because it is the one field most people never touch: an account
                with a single role behaves identically whether this is set or not. Offering every
                role rather than only this account's is deliberate - a list that narrowed as soon as
                an address was typed would answer "which roles does this address hold" to anyone who
                could type one. A role the account does not hold is refused after the code. */}
            <label className="block">
              <span className="label">Sign in as</span>
              <select
                id="role"
                name="role"
                value={role}
                onChange={(e) => setRole(e.target.value as EntityKind | '')}
                className="input"
              >
                <option value="">Continue where I left off</option>
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.kind} value={option.kind}>
                    {option.label} - {option.hint}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-ink-500">
                Only needed if you hold more than one role.
              </span>
            </label>

            <ErrorMessage message={error} />
            {notRegistered && (
              <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                <strong className="break-all">{email.trim()}</strong> is not registered on YellowDots.{' '}
                <Link to="/register" className="font-semibold text-ink-900 underline underline-offset-2">
                  Sign up first
                </Link>
                .
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-sun w-full">
              {submitting ? 'Sending...' : 'Send join code'}
            </button>
          </form>
        )}

        {/* Under the form, where someone without an account looks for it. */}
        <p className="mt-4 border-t border-ink-100 pt-3.5 text-center text-sm text-ink-500">
          New here?{' '}
          <Link
            to="/register"
            className="font-semibold text-ink-900 underline-offset-4 hover:underline"
          >
            Join the network
          </Link>
        </p>
        {/* Organisations sign in somewhere else entirely - see AggregatorPortalAuthPage. */}
        <p className="mt-2 text-center text-xs text-ink-500">
          NGO, skilling centre or community organisation?{' '}
          <Link to="/aggregator-portal/login" className="font-semibold text-ink-900 underline-offset-4 hover:underline">
            Partner Portal
          </Link>
        </p>

        <div className="mt-6 border-t border-ink-100 pt-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">Get the app</p>
          <StoreBadges className="mt-3 justify-center" />
        </div>
      </div>
    </AuthLayout>
  )
}
